import React, { createContext, useState, useMemo } from 'react';
import { 
  CalendarClock, FolderKanban, FolderLock, UserSquare, ListChecks, 
  BookOpen, Bot, School, FileSignature, MessagesSquare, Wrench, 
  Activity, BrainCircuit, Heart, LayoutDashboard, Calendar, Users, 
  ShieldAlert, Headphones, FileText, FileDown, Zap, TrendingUp, Package, AlertCircle,
  MessageCircle, Settings2, Mail
} from 'lucide-react';

export const PatientContext = createContext(null);

export const allTabsConfig = {
  // Módulos Globais do Menu
  'dashboard': { label: 'Dashboard', icon: LayoutDashboard },
  'calendar': { label: 'Agenda', icon: Calendar },
  'patients': { label: 'Pacientes', icon: Users },
  'webmail': { label: 'Webmail Clínico', icon: Mail },
  'omnichannel': { label: 'Central WhatsApp', icon: MessageCircle },
  'whatsappControl': { label: 'Central WhatsApp', icon: MessageCircle },
  'whatsappSofiaConfig': { label: 'Gestão WhatsApp & Sofia', icon: Settings2 },
  'crm': { label: 'CRM', icon: Users },
  'supervision': { label: 'Supervisão', icon: ShieldAlert },
  'audiobooks': { label: 'Audiobooks', icon: Headphones },

  // Abas Globais de Detalhes Financeiros
  'forecastDetails': { label: 'Previsões', icon: TrendingUp },
  'unearnedRevenue': { label: 'Créditos', icon: Package },
  'totalDebt': { label: 'Débitos', icon: AlertCircle },

  // Abas de Prontuário
  'patientDetail': { label: 'Prontuário', icon: UserSquare },
  'sessions': { label: 'Sessões', icon: CalendarClock },
  'documents': { label: 'Documentos', icon: FolderKanban },
  'privateFiles': { label: 'Arquivos Privados', icon: FolderLock },
  'registration': { label: 'Cadastro', icon: UserSquare },
  'tasks': { label: 'Tarefas', icon: ListChecks },
  'history': { label: 'Histórico', icon: BookOpen },
  'preparation': { label: 'IA: Preparação', icon: Bot },
  'supervisions': { label: 'Supervisões', icon: Bot },
  'caseStudy': { label: 'IA: Estudo de Caso', icon: School },
  'inventory': { label: 'Inventário de Testes', icon: FileSignature },
  'informantSubmissions': { label: 'Informantes', icon: MessagesSquare },
  'tools': { label: 'Ferramentas', icon: Wrench },
  'toolAnalysis': { label: 'Análise', icon: Activity },
  'psychometricTests': { label: 'Testes', icon: BrainCircuit },
  'sentiment': { label: 'Sentimento', icon: Heart },
  'integratedReport': { label: 'Laudo Integrado', icon: FileText },
  'atestado': { label: 'Gerador de Atestado', icon: FileDown },
  'batchGeneration': { label: 'Geração em Lote', icon: Zap },
  'editor': { label: 'Apuração de Teste', icon: Wrench },
  'viewer': { label: 'Visualizar Resultados', icon: BookOpen },
  'generator': { label: 'Gerar Laudo', icon: Bot },
};

export const PatientProvider = ({ children }) => {
  const [currentPatientId, setCurrentPatientId] = useState(null);
  
  // Floating Windows State (Global)
  // One-time cleanup: clear stuck windows from previous buggy version
  const [floatingWindows, setFloatingWindows] = useState(() => {
    const cleanupDone = localStorage.getItem('psm_floating_windows_cleanup_v2');
    if (!cleanupDone) {
      sessionStorage.removeItem('psm_floating_windows');
      localStorage.setItem('psm_floating_windows_cleanup_v2', 'true');
      return [];
    }
    try {
      const saved = sessionStorage.getItem('psm_floating_windows');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [taskbarPos, setTaskbarPos] = useState(() => {
    const saved = localStorage.getItem('psm_taskbar_pos');
    // Nascer na direita por padrão se não houver salvo
    return saved ? JSON.parse(saved) : { bottom: 100, left: window.innerWidth - 80, orientation: 'vertical' };
  });

  const handlePopOut = (tabKey, patientId = null, patientName = null, data = null) => {
    setFloatingWindows(prev => {
        const windowId = data?.id ? `${tabKey}-${patientId}-${data.id}` : (patientId ? `${tabKey}-${patientId}` : tabKey);
        
        if (prev.find(w => w.id === windowId)) {
            return prev.map(w => w.id === windowId ? { ...w, isMinimized: false } : w);
        }
        
        const offset = prev.length * 30;
        const newWindow = {
            id: windowId,
            tabKey: tabKey,
            patientId,
            patientName: patientName || '',
            title: patientName ? `${allTabsConfig[tabKey]?.label || 'Gerador'} - ${patientName}` : (allTabsConfig[tabKey]?.label || 'Gerador'),
            data,
            isMinimized: false,
            isMaximized: tabKey === 'integratedReport', // Abrir maximizado se for Laudo Integrado
            pos: { x: 100 + offset, y: 100 + offset },
            size: { width: 1000, height: 750 }
        };
        
        return [...prev, newWindow];
    });
  };

  // Janelas abertas ficam salvas POR ABA (sessionStorage): sobrevivem ao refresh sem uma aba abrir as janelas da outra
  React.useEffect(() => {
    sessionStorage.setItem('psm_floating_windows', JSON.stringify(floatingWindows));
  }, [floatingWindows]);

  const value = useMemo(() => ({
    currentPatientId,
    setCurrentPatientId,
    floatingWindows,
    setFloatingWindows,
    taskbarPos,
    setTaskbarPos,
    allTabsConfig,
    handlePopOut
  }), [currentPatientId, floatingWindows, taskbarPos]);

  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
};
